import React from "react";

import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import MessageIcon from "@mui/icons-material/Message";
import SpeedIcon from "@mui/icons-material/Speed";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

import { ORGANIZATIONS } from "src/platform/constants/routes";
import { useActiveOrganization } from "src/platform/hooks/useActiveOrganization";
import { EmptyState } from "src/shell/components/EmptyState";
import { ErrorMessage } from "src/shell/components/ErrorMessage";
import {
  getTableContainerSx,
  getTableHeadRowSx,
  sectionLabelSx,
} from "src/shell/styles/themeUtils";

import type { StackBillingBreakdown } from "../../types";

interface CostsTabProps {
  alias: string;
  data: StackBillingBreakdown | null;
  loading: boolean;
  error: string | null;
}

// ── Metric card ───────────────────────────────────────────────────────────────

function MetricCard({
  label,
  value,
  subtext,
  icon,
}: {
  label: string;
  value: string;
  subtext?: string;
  icon: React.ReactElement;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{ flex: 1, p: 2, borderRadius: 2, minWidth: 0 }}
    >
      <Typography
        sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", mb: 1 }}
      >
        {label}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        {icon}
        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{value}</Typography>
      </Stack>
      {subtext && (
        <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
          {subtext}
        </Typography>
      )}
    </Paper>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function CostsTab({
  alias: _alias,
  data,
  loading,
  error,
}: CostsTabProps) {
  const theme = useTheme();
  const { orgAlias } = useActiveOrganization();
  const billingTabPath = orgAlias
    ? `${ORGANIZATIONS.DETAILS.getPath(orgAlias)}?tab=billing`
    : ORGANIZATIONS.ROOT.path;

  if (loading) {
    return (
      <Box sx={{ pt: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <Skeleton
            variant="rounded"
            height={90}
            sx={{ flex: 1, borderRadius: 2 }}
          />
          <Skeleton
            variant="rounded"
            height={90}
            sx={{ flex: 1, borderRadius: 2 }}
          />
          <Skeleton
            variant="rounded"
            height={90}
            sx={{ flex: 1, borderRadius: 2 }}
          />
        </Stack>
        <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <ErrorMessage message={`Failed to load billing data: ${error}`} />
      </Box>
    );
  }

  if (!data || data.dataQuality === "empty") {
    return (
      <EmptyState
        icon={AttachMoneyIcon}
        title="No usage recorded yet"
        subtitle="Costs will appear here once the stack has been running for a few minutes."
      />
    );
  }

  const isFree = data.plan === "free";
  const usage = data.freeTierUsage;

  const periodLabel = (() => {
    const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
    return `${new Date(data.periodStart).toLocaleDateString("en-US", opts)} – ${new Date(data.periodEnd).toLocaleDateString("en-US", opts)}`;
  })();

  // Compute compute and message totals from line items
  const computeItems = data.lineItems.filter((item) =>
    /CPU|Memory|Build compute|Storage/i.test(item.label),
  );
  const messageItems = data.lineItems.filter((item) =>
    /message/i.test(item.label),
  );

  const computeTotal = computeItems
    .reduce((sum, item) => sum + parseFloat(item.total || "0"), 0)
    .toFixed(2);
  const messageTotal = messageItems
    .reduce((sum, item) => sum + parseFloat(item.total || "0"), 0)
    .toFixed(2);

  const isCold = data.dataQuality === "cold";

  return (
    <Box sx={{ pt: 2 }}>
      {/* Cold state alert */}
      {isCold && (
        <Alert
          severity={isFree ? "info" : "warning"}
          icon={<WarningAmberIcon />}
          sx={{ mb: 2, borderRadius: 1.5 }}
        >
          {isFree
            ? "Free tier — usage is metered but not charged. Activate paid billing to see detailed line items."
            : "Metering warming up — line items below may be estimates until the background collector finishes reconciling usage."}
        </Alert>
      )}

      {/* 3 metric cards row */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          flexWrap: { xs: "wrap", sm: "nowrap" },
        }}
      >
        <MetricCard
          label="MONTH-TO-DATE"
          value={isFree ? "$0.00" : `$${data.totalUsd}`}
          subtext={periodLabel}
          icon={
            <AttachMoneyIcon sx={{ fontSize: 20, color: "success.main" }} />
          }
        />
        <MetricCard
          label="COMPUTE"
          value={isCold ? "—" : `$${computeTotal}`}
          subtext="CPU + Memory + Build"
          icon={<SpeedIcon sx={{ fontSize: 20, color: "info.main" }} />}
        />
        <MetricCard
          label="MESSAGES"
          value={isCold ? "—" : `$${messageTotal}`}
          subtext="Redis + EventBus"
          icon={<MessageIcon sx={{ fontSize: 20, color: "secondary.main" }} />}
        />
      </Box>

      {/* Free tier quota */}
      {isFree && usage && (
        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            p: 2,
            mb: 3,
          }}
        >
          <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 1.5 }}>
            Free tier usage
          </Typography>

          <Stack spacing={2}>
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mb: 0.5 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Stacks
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color={
                    usage.activeStackCount >= usage.stackLimit
                      ? "error.main"
                      : "text.primary"
                  }
                >
                  {usage.activeStackCount} / {usage.stackLimit}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.min(
                  (usage.activeStackCount / usage.stackLimit) * 100,
                  100,
                )}
                color={
                  usage.activeStackCount >= usage.stackLimit
                    ? "error"
                    : "primary"
                }
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>

            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mb: 0.5 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Compute hours (MTD)
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color={
                    usage.computeHoursUsed >= usage.computeHoursLimit
                      ? "error.main"
                      : "text.primary"
                  }
                >
                  {usage.computeHoursUsed}h / {usage.computeHoursLimit}h
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.min(
                  (usage.computeHoursUsed / usage.computeHoursLimit) * 100,
                  100,
                )}
                color={
                  usage.computeHoursUsed >= usage.computeHoursLimit
                    ? "error"
                    : "primary"
                }
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          </Stack>

          {data.quotaExhausted && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.error.main, 0.06),
                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="error.main"
                  >
                    Free tier stack limit reached
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Activate paid billing to deploy additional stacks. Usage is
                    metered and billed per CPU/RAM hour — no flat monthly fee.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<CreditCardIcon />}
                  href={billingTabPath}
                  sx={{ flexShrink: 0, textTransform: "none" }}
                >
                  Activate billing
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      )}

      <BillingDetails data={data} />
    </Box>
  );
}

/** Reusable line-items table. */
function BillingDetails({ data }: { data: StackBillingBreakdown }) {
  const theme = useTheme();
  const isFree = data.plan === "free";
  if (data.lineItems.length === 0) return null;

  return (
    <Box sx={getTableContainerSx(theme)}>
      <Table size="small">
        <TableHead>
          <TableRow sx={getTableHeadRowSx(theme)}>
            <TableCell>Line item</TableCell>
            <TableCell>Quantity</TableCell>
            <TableCell>Unit price</TableCell>
            <TableCell align="right">Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.lineItems.map((item) => (
            <TableRow key={item.label}>
              <TableCell>{item.label}</TableCell>
              <TableCell sx={{ color: "text.secondary" }}>
                {item.quantity}
              </TableCell>
              <TableCell sx={{ color: "text.secondary" }}>
                {item.unitPrice}
              </TableCell>
              <TableCell align="right">
                {isFree ? (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.disabled"
                  >
                    {item.total}
                  </Typography>
                ) : (
                  item.total
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
